import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { EventService } from '@planner/types'

export function useEventServices(eventId: string) {
  const [eventServices, setEventServices] = useState<EventService[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchEventServices = useCallback(async () => {
    if (!eventId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('event_services')
        .select(`
          *,
          service:services (
            *,
            service_category:service_categories (*)
          )
        `)
        .eq('event_id', eventId)
        .order('created_at', { ascending: true })

      if (fetchError) {
        setError(fetchError.message)
      } else {
        setEventServices(data || [])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd')
    } finally {
      setLoading(false)
    }
  }, [eventId])

  useEffect(() => {
    fetchEventServices()
  }, [fetchEventServices])

  const addEventService = useCallback(async (
    serviceId: string,
    quantity: number,
    unitPrice: number
  ) => {
    try {
      const totalPrice = quantity * unitPrice

      const { data, error: insertError } = await supabase
        .from('event_services')
        .upsert({
          event_id: eventId,
          service_id: serviceId,
          quantity,
          unit_price: unitPrice,
          total_price: totalPrice,
        }, {
          onConflict: 'event_id,service_id'
        })
        .select(`
          *,
          service:services (
            *,
            service_category:service_categories (*)
          )
        `)
        .single()

      if (insertError) {
        return { error: insertError.message, data: null }
      }

      await fetchEventServices()
      return { error: null, data }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd', data: null }
    }
  }, [eventId, fetchEventServices])

  const updateEventService = useCallback(async (
    serviceId: string,
    quantity: number,
    unitPrice: number
  ) => {
    try {
      const totalPrice = quantity * unitPrice

      const { data, error: updateError } = await supabase
        .from('event_services')
        .update({
          quantity,
          unit_price: unitPrice,
          total_price: totalPrice,
        })
        .eq('event_id', eventId)
        .eq('service_id', serviceId)
        .select(`
          *,
          service:services (
            *,
            service_category:service_categories (*)
          )
        `)
        .single()

      if (updateError) {
        return { error: updateError.message, data: null }
      }

      await fetchEventServices()
      return { error: null, data }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd', data: null }
    }
  }, [eventId, fetchEventServices])

  const removeEventService = useCallback(async (serviceId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('event_services')
        .delete()
        .eq('event_id', eventId)
        .eq('service_id', serviceId)

      if (deleteError) {
        return { error: deleteError.message }
      }

      await fetchEventServices()
      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [eventId, fetchEventServices])

  const calculateTotal = useCallback(() => {
    return eventServices.reduce((sum, es) => sum + es.total_price, 0)
  }, [eventServices])

  return {
    eventServices,
    loading,
    error,
    fetchEventServices,
    addEventService,
    updateEventService,
    removeEventService,
    calculateTotal,
  }
}


